import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchPerfume, adminUpdatePerfume } from "../lib/api";
import { labels, GENDERS, AVAILABILITIES, SEASONS, CONDITIONS, SILLAGES, LONGEVITIES, CONCENTRATIONS } from "../i18n/translations";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { compressImage } from "../lib/image";
import { X } from "lucide-react";

const Field = ({ label, children }) => (
  <div className="mb-6">
    <label className="block uppercase tracking-[0.22em] text-[10px] text-black/50 mb-2">
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full border-0 border-b border-black/20 bg-transparent py-2 focus:outline-none focus:border-black text-sm";
const textareaCls =
  "w-full border border-black/15 bg-white p-3 focus:outline-none focus:border-black text-sm font-light leading-relaxed resize-y";

export const AdminEditPerfume = () => {
  const { user, loading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (id) {
      fetchPerfume(id)
        .then((p) => mounted && setForm(p))
        .catch(() => mounted && setForm(false));
    }
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading)
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="uppercase tracking-[0.32em] text-[11px] text-black/40">…</div>
      </main>
    );
  if (!user || user === false) return <Navigate to="/admin/login" replace />;
  if (form === false)
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 px-6">
        <div className="font-heading text-3xl">Profumo non trovato.</div>
        <Link to="/admin" className="uppercase tracking-[0.22em] text-[11px] border-b border-black pb-1">
          ← Torna alla lista
        </Link>
      </main>
    );
  if (!form)
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="uppercase tracking-[0.32em] text-[11px] text-black/40">…</div>
      </main>
    );

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onUploadMain = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, { maxWidth: 1400, quality: 0.85 });
      update({ main_image: dataUrl });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onUploadAdditional = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const arr = [...(form.additional_images || [])];
      arr[index] = dataUrl;
      update({ additional_images: arr.slice(0, 3) });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAdditional = (index) => {
    const arr = [...(form.additional_images || [])];
    arr.splice(index, 1);
    update({ additional_images: arr });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.slot_number;
      delete payload.updated_at;
      const saved = await adminUpdatePerfume(id, payload);
      setForm(saved);
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  const displayName = form.name || `Profumo ${form.slot_number}`;
  const additional = form.additional_images || [];

  return (
    <main data-testid="page-admin-edit" className="min-h-screen bg-white">
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-black/5 px-6 md:px-12 lg:px-20 py-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/admin"
            data-testid="admin-back"
            className="uppercase tracking-[0.22em] text-[10px] text-black/50 hover:text-[#C5A059] transition-colors"
          >
            {labels.admin.back_to_list}
          </Link>
          <div className="font-heading text-xl md:text-2xl tracking-tight truncate">
            #{form.slot_number} · {displayName}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && (
            <span className="uppercase tracking-[0.22em] text-[10px] text-[#C5A059]">
              {labels.admin.saved} · {savedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving || uploading}
            data-testid="admin-save"
            className="bg-black text-white hover:bg-[#C5A059] transition-colors uppercase tracking-[0.22em] text-[10px] py-3 px-6 disabled:opacity-50"
          >
            {saving ? labels.admin.saving : labels.admin.save}
          </button>
        </div>
      </div>

      <section className="px-6 md:px-12 lg:px-20 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="uppercase tracking-[0.32em] text-[10px] text-[#C5A059] mb-4">
            {labels.admin.images_section}
          </div>

          <div className="aspect-[4/5] bg-[#F4F4F4] overflow-hidden mb-3 relative group">
            {form.main_image ? (
              <img src={form.main_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImagePlaceholder />
            )}
            {form.main_image && (
              <button
                type="button"
                onClick={() => update({ main_image: "" })}
                className="absolute top-3 right-3 bg-white/90 hover:bg-white p-2 transition-colors"
                title={labels.admin.remove}
                data-testid="admin-remove-main"
              >
                <X strokeWidth={1.25} className="w-4 h-4" />
              </button>
            )}
          </div>
          <label
            className="block w-full text-center cursor-pointer bg-black text-white hover:bg-[#C5A059] transition-colors uppercase tracking-[0.22em] text-[10px] py-3"
            data-testid="admin-upload-main-label"
          >
            {labels.admin.upload_main}
            <input
              type="file"
              accept="image/*"
              onChange={onUploadMain}
              className="hidden"
              data-testid="admin-upload-main-input"
            />
          </label>

          <div className="mt-8 uppercase tracking-[0.32em] text-[10px] text-[#C5A059] mb-3">
            {labels.admin.additional_images}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => {
              const img = additional[i];
              return (
                <div key={i} className="space-y-2">
                  <div className="aspect-square bg-[#F4F4F4] overflow-hidden relative group">
                    {img ? (
                      <>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeAdditional(i)}
                          className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 transition-colors"
                          data-testid={`admin-remove-additional-${i}`}
                        >
                          <X strokeWidth={1.25} className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <ImagePlaceholder label="" />
                    )}
                  </div>
                  <label
                    className="block w-full text-center cursor-pointer border border-black/20 hover:border-black hover:bg-black hover:text-white transition-colors uppercase tracking-[0.22em] text-[9px] py-2"
                    data-testid={`admin-upload-additional-label-${i}`}
                  >
                    + Foto
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onUploadAdditional(e, i)}
                      className="hidden"
                      data-testid={`admin-upload-additional-input-${i}`}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="uppercase tracking-[0.32em] text-[10px] text-[#C5A059] mb-4">
            {labels.admin.fields_section}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <Field label={labels.admin.field_labels.brand}>
              <input type="text" value={form.brand || ""} onChange={(e) => update({ brand: e.target.value })} className={inputCls} data-testid="field-brand" />
            </Field>
            <Field label={labels.admin.field_labels.name}>
              <input type="text" value={form.name || ""} onChange={(e) => update({ name: e.target.value })} className={inputCls} data-testid="field-name" />
            </Field>
            <Field label={labels.admin.field_labels.size_ml}>
              <input type="text" value={form.size_ml || ""} onChange={(e) => update({ size_ml: e.target.value })} className={inputCls} data-testid="field-size" />
            </Field>
            <Field label={labels.admin.field_labels.condition}>
              <select value={form.condition || ""} onChange={(e) => update({ condition: e.target.value })} className={inputCls} data-testid="field-condition">
                <option value="">—</option>
                {CONDITIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.condition && !CONDITIONS.includes(form.condition) && (
                  <option value={form.condition}>{form.condition}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.price}>
              <input type="text" value={form.price || ""} onChange={(e) => update({ price: e.target.value })} className={inputCls} data-testid="field-price" />
            </Field>
            <Field label={labels.admin.field_labels.availability}>
              <select value={form.availability || ""} onChange={(e) => update({ availability: e.target.value })} className={inputCls} data-testid="field-availability">
                <option value="">—</option>
                {AVAILABILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.availability && !AVAILABILITIES.includes(form.availability) && (
                  <option value={form.availability}>{form.availability}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.gender}>
              <select value={form.gender || ""} onChange={(e) => update({ gender: e.target.value })} className={inputCls} data-testid="field-gender">
                <option value="">—</option>
                {GENDERS.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.gender && !GENDERS.includes(form.gender) && (
                  <option value={form.gender}>{form.gender}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.year}>
              <input type="text" value={form.year || ""} onChange={(e) => update({ year: e.target.value })} className={inputCls} data-testid="field-year" />
            </Field>
            <Field label={labels.admin.field_labels.concentration}>
              <select value={form.concentration || ""} onChange={(e) => update({ concentration: e.target.value })} className={inputCls} data-testid="field-concentration">
                <option value="">—</option>
                {CONCENTRATIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.concentration && !CONCENTRATIONS.includes(form.concentration) && (
                  <option value={form.concentration}>{form.concentration}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.seasonality}>
              <select value={form.seasonality || ""} onChange={(e) => update({ seasonality: e.target.value })} className={inputCls} data-testid="field-seasonality">
                <option value="">—</option>
                {SEASONS.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.seasonality && !SEASONS.includes(form.seasonality) && (
                  <option value={form.seasonality}>{form.seasonality}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.longevity}>
              <select value={form.longevity || ""} onChange={(e) => update({ longevity: e.target.value })} className={inputCls} data-testid="field-longevity">
                <option value="">—</option>
                {LONGEVITIES.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.longevity && !LONGEVITIES.includes(form.longevity) && (
                  <option value={form.longevity}>{form.longevity}</option>
                )}
              </select>
            </Field>
            <Field label={labels.admin.field_labels.sillage}>
              <select value={form.sillage || ""} onChange={(e) => update({ sillage: e.target.value })} className={inputCls} data-testid="field-sillage">
                <option value="">—</option>
                {SILLAGES.map((v) => <option key={v} value={v}>{v}</option>)}
                {form.sillage && !SILLAGES.includes(form.sillage) && (
                  <option value={form.sillage}>{form.sillage}</option>
                )}
              </select>
            </Field>
          </div>

          <Field label={labels.admin.field_labels.olfactory_notes}>
            <textarea rows={3} value={form.olfactory_notes || ""} onChange={(e) => update({ olfactory_notes: e.target.value })} className={textareaCls} data-testid="field-notes" />
          </Field>

          <Field label={labels.admin.field_labels.short_description}>
            <textarea rows={3} value={form.short_description || ""} onChange={(e) => update({ short_description: e.target.value })} className={textareaCls} data-testid="field-short" />
          </Field>

          <Field label={labels.admin.field_labels.full_description}>
            <textarea rows={8} value={form.full_description || ""} onChange={(e) => update({ full_description: e.target.value })} className={textareaCls} data-testid="field-full" />
          </Field>

          <div className="my-8 border-t border-black/10 pt-8">
            <div className="uppercase tracking-[0.32em] text-[10px] text-[#C5A059] mb-4">
              Le mie selezioni
            </div>
            <label className="inline-flex items-center gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={Boolean(form.featured_in_selections)}
                onChange={(e) => update({ featured_in_selections: e.target.checked })}
                data-testid="field-featured-selections"
                className="w-4 h-4 accent-[#C5A059]"
              />
              <span className="uppercase tracking-[0.22em] text-[10px] text-black/60">
                {labels.admin.field_labels.featured_in_selections}
              </span>
            </label>

            <Field label={labels.admin.field_labels.personal_recommendation}>
              <textarea
                rows={3}
                value={form.personal_recommendation || ""}
                onChange={(e) => update({ personal_recommendation: e.target.value })}
                className={textareaCls}
                data-testid="field-personal-recommendation"
                placeholder="Es. Un legno fumé con un'apertura sorprendente. Il mio preferito d'inverno."
              />
            </Field>
          </div>

          <div className="mt-2 mb-10">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(form.visible)}
                onChange={(e) => update({ visible: e.target.checked })}
                data-testid="field-visible"
                className="w-4 h-4 accent-[#C5A059]"
              />
              <span className="uppercase tracking-[0.22em] text-[10px] text-black/60">
                {labels.admin.field_labels.visible}
              </span>
            </label>
          </div>

          <button
            type="button"
            onClick={onSave}
            disabled={saving || uploading}
            data-testid="admin-save-bottom"
            className="w-full md:w-auto bg-black text-white hover:bg-[#C5A059] transition-colors uppercase tracking-[0.22em] text-[10px] py-4 px-10 disabled:opacity-50"
          >
            {saving ? labels.admin.saving : labels.admin.save}
          </button>
        </div>
      </section>
    </main>
  );
};

export default AdminEditPerfume;
